import type { Initiative } from '../lib/types';
import { CATEGORY_LABEL } from '../lib/types';
import { updateInitiativeStatus } from '../hooks/useRoadmap';
import { useState } from 'react';

interface Props {
  initiative: Initiative;
  onClick: () => void;
}

export default function InitiativeRow({ initiative, onClick }: Props) {
  const [toggling, setToggling] = useState(false);

  async function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    setToggling(true);
    try {
      const next = initiative.status === 'done' ? 'in_progress' : 'done';
      await updateInitiativeStatus(initiative.id, next);
      // Optimistic parent refresh happens via modal; here we just update local style
      initiative.status = next;
      initiative.completed_at = next === 'done' ? new Date().toISOString() : null;
    } finally {
      setToggling(false);
    }
  }

  return (
    <div
      className="group flex items-start gap-2 px-2.5 py-2 rounded-md border border-white/5 bg-ink-800/60 hover:bg-ink-800 cursor-pointer transition"
      onClick={onClick}
    >
      <button
        onClick={toggle}
        className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
          initiative.status === 'done'
            ? 'bg-emerald-500/80 border-emerald-400'
            : 'border-ink-400/50 hover:border-ink-200'
        }`}
        disabled={toggling}
        aria-label="toggle done"
      >
        {initiative.status === 'done' && (
          <svg viewBox="0 0 12 12" className="w-3 h-3 text-white">
            <path d="M2 6.5L5 9.5L10 3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`chip chip-${initiative.priority.toLowerCase()}`}>{initiative.priority}</span>
          <span className={`chip chip-${initiative.status}`}>{CATEGORY_LABEL[initiative.category]}</span>
          <span className={`text-sm ${initiative.status === 'done' ? 'line-through text-ink-400' : 'text-ink-100'}`}>
            {initiative.title}
          </span>
        </div>
        {(initiative.target_date || initiative.blockers || initiative.owner) && (
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-ink-400">
            {initiative.owner && <span>@ {initiative.owner}</span>}
            {initiative.target_date && <span>🎯 {initiative.target_date}</span>}
            {initiative.blockers && <span className="text-rose-300">⛔ {initiative.blockers}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
